"use client"

import { createContext, useState, useEffect, useContext, useCallback, useRef } from "react"
import { authService } from "../services/api.service"

// Create context with default value
const AuthContext = createContext({
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  updateUserData: () => {},
  refreshUserData: async () => {},
  clearError: () => {},
})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initRef = useRef(false)

  // Helper function to process user data
  const processUserData = useCallback((userData) => {
    if (!userData) return null

    return {
      ...userData,
      // Ensure leave_balance and sick_leave_balance are properly set
      leave_balance: Number(userData.leave_balance || userData.leaveBalance || 0),
      sick_leave_balance: Number(userData.sick_leave_balance || userData.sickLeaveBalance || 0),
    }
  }, [])

  // Refresh user data from the API
  const refreshUserData = useCallback(async () => {
    try {
      console.log("🔄 Refreshing user data from API...")
      setError(null)

      const { user: freshUserData } = await authService.getCurrentUser()
      console.log("✅ Fresh user data from API:", freshUserData)

      const userData = processUserData(freshUserData)
      console.log("✅ Processed user data:", userData)

      setUser(userData)

      // Also update local storage to keep it in sync
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData))
      }

      return userData
    } catch (err) {
      console.error("❌ Error refreshing user data:", err)

      // If it's an auth error, logout the user
      if (err.response?.status === 401) {
        console.log("🔐 Token expired, logging out...")
        authService.logout()
        setUser(null)
      }

      throw err
    }
  }, [processUserData])

  // Initialize auth state - FIXED to satisfy ESLint
  const initializeAuth = useCallback(async () => {
    // Prevent multiple initializations
    if (initRef.current) return
    initRef.current = true

    try {
      console.log("🔄 Initializing auth...")
      setError(null)

      // Check if user is already logged in
      if (authService.isAuthenticated()) {
        const storedUser = authService.getUser()
        console.log("📱 Found stored user:", storedUser?.email)

        if (storedUser) {
          // Set stored user data first
          const processedUser = processUserData(storedUser)
          setUser(processedUser)
          setLoading(false) // Set loading false immediately with stored data
          
          console.log("✅ Set stored user data, loading = false")

          // Try to fetch fresh user data in the background
          try {
            console.log("🔄 Fetching fresh data in background...")
            await refreshUserData()
          } catch (err) {
            console.warn("⚠️ Failed to refresh user data, using stored data:", err.message)
            // Keep the stored user data if API call fails
          }
        } else {
          console.log("❌ No valid stored user found")
          setLoading(false)
        }
      } else {
        console.log("🔐 User not authenticated")
        setLoading(false)
      }
    } catch (err) {
      console.error("❌ Auth initialization error:", err)
      setError("Failed to initialize authentication")
      // Clear potentially corrupted data
      authService.logout()
      setUser(null)
      setLoading(false)
    }
  }, [processUserData, refreshUserData])

  // Run initialization once on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // Login function
  const login = async (credentials) => {
    try {
      console.log("🔐 Logging in user:", credentials.email)
      setLoading(true)
      setError(null)

      const data = await authService.login(credentials)

      // Ensure correct property mapping
      const userData = processUserData(data.user)

      console.log("✅ User data after login:", userData)
      setUser(userData)

      return data
    } catch (err) {
      console.error("❌ Login error:", err)

      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || "Login failed. Please check your credentials."

      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Register function
  const register = async (userData) => {
    try {
      console.log("📝 Registering user:", userData.email)
      setLoading(true)
      setError(null)

      const data = await authService.register(userData)

      // Ensure correct property mapping
      const processedUserData = processUserData(data.user)
      console.log("✅ User data after registration:", processedUserData)

      setUser(processedUserData)
      return data
    } catch (err) {
      console.error("❌ Registration error:", err)

      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || "Registration failed. Please try again."

      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Logout function
  const logout = useCallback(() => {
    console.log("🔐 Logging out user")
    authService.logout()
    setUser(null)
    setError(null)
    initRef.current = false // Reset initialization flag
  }, [])

  // Update user data
  const updateUserData = useCallback((userData) => {
    console.log("🔄 Updating user data:", userData)
    const processedUserData = processUserData(userData)
    setUser(processedUserData)

    if (processedUserData) {
      localStorage.setItem("user", JSON.stringify(processedUserData))
    }
  }, [processUserData])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateUserData,
    refreshUserData,
    clearError,
  }

  // Debug logging
  console.log("🔍 AuthContext state:", { 
    hasUser: !!user, 
    loading, 
    initialized: initRef.current, 
    isAuthenticated: !!user,
    userEmail: user?.email 
  })

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export default AuthContext